import { parseLocationInput, type CoordinateError } from '@reos/geo-input';
import { fetchMapLink } from './map-link-api.js';

/**
 * Krok GPS1 — VSTUP POLOHY V PRUVODCI „NOVA ZAKAZKA" (krok 1, blok Adresa).
 *
 * Tri zpusoby, jak se k pozemku dostat, vedle kliknuti do mapy (krok M1):
 *
 *  1. souradnice textem — makler dostane souradnice z mobilu a vypise je,
 *  2. odkaz z mapy — kolega posle `goo.gl/…` nebo odkaz z Mapy.cz,
 *  3. „Moje poloha" — makler stoji na pozemku s telefonem.
 *
 * Vsechny tri vedou do STEJNE cesty jako klik: `handleMapPick` v
 * `zakazka-novy.tsx` nastavi bod, prevede ho do S-JTSK a K0c resolver
 * (`GET …/map/parcel-at-point`) navrhne parcelu. **Navrh NEZUSTAVA
 * automaticky potvrdeny** — makler ji potvrdi tlacitkem, stejne jako u
 * kliku. Tim se pravidlo „nepotvrzeny bod do objednavky nejde" (krok W5)
 * nerozsiruje na novy vstup.
 */

/** Jak to uzivatel zadal — UI z toho udela napovedu. */
export type LocationEntry =
  | {
      readonly kind: 'point';
      readonly lat: number;
      readonly lon: number;
      /** GPS z mobilu ma presnost v metrech; u souradnice neni co merit. */
      readonly accuracy: number | null;
    }
  /** Kratky odkaz se prave rozbija na serveru. */
  | { readonly kind: 'expanding' }
  /** Vstup vypada jako souradnice, ale neni platny. */
  | { readonly kind: 'error'; readonly code: CoordinateError }
  /** Kratky odkaz se neda rozbalit — rucni zadani zustava dostupne. */
  | { readonly kind: 'unavailable' }
  /** Vstup neni souradnice ani odkaz — neni to chyba, je to jiny druh udaje. */
  | { readonly kind: 'ignored' };

/** Zdroj chyby prohlizece — rozlisuje „prohlizec neumi" a „nedal svoleni". */
export type LocateFailure = 'unsupported' | 'denied' | 'failed';

/**
 *
 * Rozpozna souradnici / odkaz z mapy. Ciste, bez site — kratky odkaz vrati
 * `expanding` a rozbi ho volajici, ktery ma pristup k API.
 */
export function classifyLocationEntry(raw: string): LocationEntry {
  const parsed = parseLocationInput(raw);
  if (parsed.kind === 'point') {
    return { kind: 'point', lat: parsed.point.lat, lon: parsed.point.lon, accuracy: null };
  }
  if (parsed.kind === 'short') return { kind: 'expanding' };
  if (parsed.kind === 'error') return { kind: 'error', code: parsed.code };
  return { kind: 'ignored' };
}

/** Chyba prohlizece pri zjistovani polohy. */
export function locateFailureOf(error: unknown): LocateFailure {
  const code = (error as { code?: number } | null)?.code;
  // GeolocationPositionError: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE,
  // 3 = TIMEOUT. 1 a 3 znamaji „uzivatel to zkousel a neslo to“ (nepomuze
  // pomoct opravit), 2 je „spotreboval moc" — jina rada.
  if (code === 1 || code === 3) return 'denied';
  if (code === 2) return 'failed';
  return 'failed';
}

/**
 * Krok GPS1, zadani bod 4: nad 30 m je poloha NEJISTÁ a je potřeba to říct.
 *
 * Makler stojí u vratku pozemku s telefonem: u vratku je chyba GPS 3–5 m, u
 * zástavby domu klidně 50 m a pod stromem 200 m. Bez upozornění by makler
 * opsal sousední parcelu — a nikdo by to nezahlédl, protože číslo parcely
 * vypadá věrohodně a „confirmovat" klikne bez čtení.
 *
 * ROZHODNUTÍ GPS1: bod se TIM NEMĚNÍ. Přesnost je informace pro člověka, ne
 * filtr — i bod s přesností 200 m je lepší než žádný, a je opravdu blízko.
 * Jenom se řekne „dolad'te kliknutím".
 */
export const ACCURACY_WARNING_M = 30;

/** Je přesnost slabá natolik, aby se to muselo říct? */
export function isAccuracyWeak(accuracy: number | null): boolean {
  return accuracy !== null && accuracy > ACCURACY_WARNING_M;
}

/** Zaokrouhlení přesnosti na celé metry — nikdo nechce číst „12,4837 m“. */
export function formatAccuracy(metres: number): number {
  return Math.round(metres);
}

/**
 * Serverova odpoved na kratky odkaz -> vstup pruvodce. `UNAVAILABLE` z
 * `GET /v1/public/map-link` NENI chyba, je to „timto to neda" — rucni
 * zadani (a souradnice) zustava vyzadatelne vzdy.
 */
export function entryFromMapLink(response: {
  readonly status: 'OK' | 'UNAVAILABLE';
  readonly lat?: number;
  readonly lon?: number;
}): LocationEntry {
  if (response.status === 'OK' && response.lat !== undefined && response.lon !== undefined) {
    return { kind: 'point', lat: response.lat, lon: response.lon, accuracy: null };
  }
  return { kind: 'unavailable' };
}

/** Kratky odkaz -> rozbaleny bod, nebo `unavailable`. */
export async function expandLocationLink(url: string): Promise<LocationEntry> {
  try {
    return entryFromMapLink(await fetchMapLink(url));
  } catch {
    return { kind: 'unavailable' };
  }
}
